import React from 'react'
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import {
  AcademicCapIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  PlayCircleIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  UsersIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import type { FacilitatorLiveSessionCard } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { Button } from '../../../../interaxDesignSystemComponents'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { ControlTowerParticipantTable } from './ControlTowerParticipantTable'
import { ControlTowerStatTile } from './ControlTowerStatTile'
import { useJoinTeamCall } from './useJoinTeamCall'
import { useEndActivityForTeam } from './useEndActivityForTeam'
import { useSendTeamToActivityPage } from './useSendTeamToActivityPage'
import { ControlTowerEndActivityDialog } from './ControlTowerEndActivityDialog'
import { ControlTowerSendTeamDialog } from './ControlTowerSendTeamDialog'
import { ControlTowerActionOutcomeAlert } from './ControlTowerActionOutcomeAlert'
import { ControlTowerStatusPill } from './ControlTowerStatusPill'
import { formatScheduledWindow, formatStageText, getCardStatus, getKindLabel } from './controlTowerHelpers'
import {
  buildControlTowerPillSx,
  controlTowerActionRowSx,
  controlTowerActivityTitleSx,
  controlTowerButtonIconStyle,
  controlTowerCardSx,
  controlTowerHeaderRowSx,
  controlTowerKindLabelSx,
  controlTowerMetaIconStyle,
  controlTowerMetaItemSx,
  controlTowerMetaRowSx,
  controlTowerMetaTextSx,
  controlTowerTileIconStyle,
  controlTowerTitleGroupSx,
  controlTowerTitleSx
} from './controlTowerStyles'

interface ControlTowerCardProps {
  card: FacilitatorLiveSessionCard
  onOpenLoopDetails: (card: FacilitatorLiveSessionCard) => void
}

const TILE_MIN_WIDTH_PX = 132

// What the stage line is about: a running loop, the shared tutorial, a debrief, or a clock still waiting.
const getStageIcon = (card: FacilitatorLiveSessionCard): React.ReactNode => {
  if (card.kind === 'ai_debrief') return <SparklesIcon style={controlTowerMetaIconStyle} />
  if (card.currentLoopNumber && card.currentPhase) return <PlayCircleIcon style={controlTowerMetaIconStyle} />
  if (card.groupTutorial) return <AcademicCapIcon style={controlTowerMetaIconStyle} />
  return <ClockIcon style={controlTowerMetaIconStyle} />
}

const MetaDivider: React.FC = () => (
  <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: INTERAX_COLORS.white10, mx: 0.5 }} />
)

/** One team's live game or AI debrief: status, schedule, the three live counts, and the roster with its signals. */
export const ControlTowerCard: React.FC<ControlTowerCardProps> = ({ card, onOpenLoopDetails }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null)
  const joinCall = useJoinTeamCall()
  const endActivity = useEndActivityForTeam()
  const sendTeam = useSendTeamToActivityPage()
  const status = getCardStatus(card)
  const kindLabel = getKindLabel(card, t)
  const isGame = card.kind === 'game'
  const canOpenLoopDetails = isGame && Boolean(card.loopSummaries?.length)
  const expectedCount = card.expectedCount
  const testIdPrefix = `control-tower-card-${card.cardId}`

  const sendTeamLabel = isGame
    ? t('facilitator.controlTower.sendTeamToGame', 'Send team to the game')
    : t('facilitator.controlTower.sendTeamToDebrief', 'Send team to the AI debrief')

  const openLoopDetails = () => {
    setMenuAnchor(null)
    onOpenLoopDetails(card)
  }

  const askToSendTeam = () => {
    setMenuAnchor(null)
    sendTeam.openDialog(card)
  }

  const askToEndActivityForTeam = () => {
    setMenuAnchor(null)
    endActivity.openDialog(card)
  }

  return (
    <Box data-testid={testIdPrefix} sx={controlTowerCardSx}>
      <Box sx={controlTowerHeaderRowSx}>
        <Box sx={controlTowerTitleGroupSx}>
          <Box sx={buildControlTowerPillSx(isGame ? INTERAX_COLORS.sun20 : INTERAX_COLORS.blueBackground)}>
            <Typography sx={controlTowerKindLabelSx}>{kindLabel}</Typography>
          </Box>
          <Typography sx={controlTowerTitleSx} noWrap>
            {card.teamName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <IconButton
            size="small"
            aria-label={t('facilitator.controlTower.moreActions', 'More actions for {{team}}', { team: card.teamName })}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ color: INTERAX_COLORS.white70, '&:hover': { backgroundColor: INTERAX_COLORS.white10 } }}
          >
            <EllipsisHorizontalIcon style={{ width: 20, height: 20 }} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {canOpenLoopDetails ? (
              <MenuItem onClick={openLoopDetails}>{t('facilitator.controlTower.loopDetails', 'Loop details')}</MenuItem>
            ) : null}
            <MenuItem onClick={askToSendTeam} disabled={sendTeam.isSending} data-testid={`${testIdPrefix}-send-team`}>
              {sendTeamLabel}
            </MenuItem>
            <MenuItem onClick={askToEndActivityForTeam} data-testid={`${testIdPrefix}-end-for-team`}>
              {t('facilitator.controlTower.endActivityForTeam', 'End activity for this team')}
            </MenuItem>
          </Menu>

          <ControlTowerStatusPill status={status} testId={`${testIdPrefix}-status`} />
        </Box>
      </Box>

      <Typography sx={controlTowerActivityTitleSx}>{card.activityTitle}</Typography>

      <Box sx={controlTowerMetaRowSx}>
        <Box sx={controlTowerMetaItemSx}>
          <CalendarDaysIcon style={controlTowerMetaIconStyle} />
          <Typography sx={controlTowerMetaTextSx}>{formatScheduledWindow(card)}</Typography>
        </Box>
        <MetaDivider />
        <Box sx={controlTowerMetaItemSx}>
          {getStageIcon(card)}
          <Typography data-testid={`${testIdPrefix}-stage`} sx={controlTowerMetaTextSx}>
            {formatStageText(card, t)}
          </Typography>
        </Box>
      </Box>

      {card.groupTutorial?.isOverdue ? (
        <Typography sx={{ color: INTERAX_COLORS.redError, fontSize: '13px', fontWeight: 600, mt: -0.5 }}>
          {t('facilitator.controlTower.groupTutorialOverdue', 'Taking too long — the game will move them on by itself')}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: `repeat(auto-fit, minmax(${TILE_MIN_WIDTH_PX}px, 1fr))`,
            md: `repeat(auto-fit, minmax(${TILE_MIN_WIDTH_PX}px, 1fr))`
          },
          gap: 1
        }}
      >
        <ControlTowerStatTile
          icon={<UsersIcon style={controlTowerTileIconStyle} />}
          iconColor={INTERAX_COLORS.green}
          value={`${card.inMeetCount ?? 0} / ${expectedCount}`}
          label={t('facilitator.controlTower.inMeetingTile', 'In meeting')}
          testId={`${testIdPrefix}-in-meeting`}
        />
        {isGame ? (
          <>
            <ControlTowerStatTile
              icon={<PuzzlePieceIcon style={controlTowerTileIconStyle} />}
              iconColor={INTERAX_COLORS.blue}
              value={`${card.playedCount ?? 0} / ${expectedCount}`}
              label={t('facilitator.controlTower.playedThisLoopLabel', 'Played this loop')}
              testId={`${testIdPrefix}-played`}
            />
            <ControlTowerStatTile
              icon={<ClockIcon style={controlTowerTileIconStyle} />}
              iconColor={INTERAX_COLORS.sun}
              value={card.loopLengthMinutes ? `${card.loopLengthMinutes} ${t('common.minutesShort', 'min')}` : '—'}
              label={t('facilitator.controlTower.loopLengthTile', 'Loop length')}
              testId={`${testIdPrefix}-loop-length`}
            />
          </>
        ) : null}
      </Box>

      <ControlTowerParticipantTable
        participants={card.participants ?? []}
        showPlayedColumn={isGame}
        testIdPrefix={testIdPrefix}
      />

      <ControlTowerActionOutcomeAlert outcome={joinCall.outcome} onClose={joinCall.clearOutcome} />
      <ControlTowerActionOutcomeAlert outcome={endActivity.outcome} onClose={endActivity.clearOutcome} />
      <ControlTowerActionOutcomeAlert outcome={sendTeam.outcome} onClose={sendTeam.clearOutcome} />

      <ControlTowerEndActivityDialog
        card={endActivity.cardToEnd}
        isEnding={endActivity.isEnding}
        onCancel={endActivity.cancelDialog}
        onConfirm={endActivity.confirm}
      />

      <ControlTowerSendTeamDialog
        card={sendTeam.cardToSend}
        isSending={sendTeam.isSending}
        onCancel={sendTeam.cancelDialog}
        onConfirm={sendTeam.confirm}
      />

      {isGame ? (
        <Box sx={controlTowerActionRowSx}>
          <Button
            variant="secondary"
            disabled={joinCall.isOpening}
            onClick={() => joinCall.join(card)}
            icon={<VideoCameraIcon style={controlTowerButtonIconStyle} />}
            data-testid={`${testIdPrefix}-join-call`}
          >
            {joinCall.isOpening
              ? t('facilitator.controlTower.joinCallOpening', 'Opening…')
              : t('facilitator.controlTower.joinCall', 'Join call')}
          </Button>
        </Box>
      ) : null}

      {card.kind === 'ai_debrief' ? (
        <Box sx={controlTowerActionRowSx}>
          <Button
            variant="primary"
            disabled={!card.canJoinAiDebrief || !card.aiCoachSessionId}
            onClick={() => {
              if (!card.aiCoachSessionId) return
              navigate(`/facilitator/ai-coach/${card.aiCoachSessionId}`)
            }}
            icon={<ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />}
          >
            {t('facilitator.controlTower.joinAiDebrief', 'Join AI Debrief')}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
